import { useState, useEffect, useRef } from "react";
import { db } from "../firebase";
import {
  collection,
  getDocs,
  doc,
  getDoc,
  setDoc
} from "firebase/firestore";
import { getStudiesPath, getStudyContactsPath } from "../services/firestorePaths";

import { motion } from "framer-motion";
import {
  ArrowLeft,
  Search,
  Save,
  Plus,
  Trash,
  ChevronDown,
  Phone,
  Mail,
  User,
  Layers,
  CheckCircle2,
} from "lucide-react";

const STANDARD_ROLES = [
  "Lead CRC",
  "Backup CRC",
  "Pharmacist",
  "Primary CRA",
  "Backup CRA",
  "Billing PoC"
];

const formatPhoneNumber = (value) => {
  const digits = value.replace(/\D/g, "");
  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
  return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
};

const inputCls =
  "w-full px-3 py-2 rounded-xl bg-white/[0.04] border border-white/10 text-sm text-slate-100 placeholder:text-slate-500 focus:outline-none focus:border-purple-400/50 focus:bg-white/[0.06] focus:ring-2 focus:ring-purple-500/20 transition";
const labelCls =
  "block text-[10px] uppercase tracking-wider font-medium text-slate-400 mb-1";

const PageBackground = () => (
  <div className="fixed inset-0 -z-10 overflow-hidden">
    <img
      src="/bg_ai.jpg"
      alt=""
      aria-hidden="true"
      className="absolute inset-0 w-full h-full object-cover"
    />
    <div className="absolute inset-0 bg-gradient-to-br from-[#070418]/90 via-[#0c0726]/85 to-[#04030f]/95" />
    <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_rgba(124,58,237,0.25),_transparent_55%)]" />
    <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_right,_rgba(37,99,235,0.22),_transparent_55%)]" />
    <div className="absolute -top-32 -left-32 w-[520px] h-[520px] rounded-full bg-purple-600/25 blur-[140px]" />
    <div className="absolute top-1/3 -right-40 w-[520px] h-[520px] rounded-full bg-blue-600/25 blur-[140px]" />
  </div>
);

const StudyContactsPage = () => {
  const [studies, setStudies] = useState([]);
  const [filteredStudies, setFilteredStudies] = useState([]);
  const [studySearch, setStudySearch] = useState("");
  const [selectedStudy, setSelectedStudy] = useState(null);
  const [showDropdown, setShowDropdown] = useState(false);
  const [contacts, setContacts] = useState([]);
  const [systems, setSystems] = useState([]);
  const [studyDetails, setStudyDetails] = useState(null);
  const [saving, setSaving] = useState(false);
  const dropdownRef = useRef(null);

  // ---- Fetch studies ----
  useEffect(() => {
    const fetchStudies = async () => {
      const snap = await getDocs(collection(db, getStudiesPath()));
      const data = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      const formatted = data.map((s) => ({
        id: s.STUDY_ID,
        label: `${s.NAME} – ${s.STUDY_ID}${s.STATUS ? ` (${s.STATUS})` : ""}`
      }));
      setStudies(formatted);
      setFilteredStudies(formatted);
    };
    fetchStudies();
  }, []);

  // ---- Study search filter ----
  useEffect(() => {
    const lower = studySearch.toLowerCase();
    setFilteredStudies(studies.filter((s) => s.label.toLowerCase().includes(lower)));
  }, [studySearch, studies]);

  // ---- Close dropdown on outside click ----
  useEffect(() => {
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // ---- Load study contacts ----
  const handleStudySelect = async (studyId) => {
    setSelectedStudy(studyId);
    setShowDropdown(false);
    const studyRef = doc(db, getStudiesPath(), studyId);
    const studySnap = await getDoc(studyRef);
    if (studySnap.exists()) setStudyDetails(studySnap.data());
    const ref = doc(db, getStudyContactsPath(), studyId);
    const snap = await getDoc(ref);
    if (snap.exists()) {
      const data = snap.data();
      setContacts(data.contacts || []);
      setSystems(data.systems || []);
    } else {
      setContacts([]);
      setSystems([]);
    }
  };

  // ---- Contact handlers ----
  const addContact = () =>
    setContacts([...contacts, { role: "", name: "", email: "", phones: [{ country: "+1", number: "" }] }]);

  const updateContact = (i, field, value) => {
    const updated = [...contacts];
    updated[i][field] = value;
    setContacts(updated);
  };

  const deleteContact = (i) => setContacts(contacts.filter((_, idx) => idx !== i));

  const addPhone = (contactIndex) => {
    const updated = [...contacts];
    updated[contactIndex].phones.push({ country: "+1", number: "" });
    setContacts(updated);
  };

  const updatePhone = (contactIndex, phoneIndex, field, value) => {
    const updated = [...contacts];
    if (field === "number") value = formatPhoneNumber(value);
    updated[contactIndex].phones[phoneIndex][field] = value;
    setContacts(updated);
  };

  const deletePhone = (contactIndex, phoneIndex) => {
    const updated = [...contacts];
    updated[contactIndex].phones.splice(phoneIndex, 1);
    setContacts(updated);
  };

  // ---- Systems ----
  const addSystem = () => setSystems([...systems, ""]);
  const updateSystem = (i, value) => {
    const updated = [...systems];
    updated[i] = value;
    setSystems(updated);
  };
  const deleteSystem = (i) => setSystems(systems.filter((_, idx) => idx !== i));

  // ---- Save ----
  const handleSave = async () => {
    if (!selectedStudy) return;
    setSaving(true);
    try {
      await setDoc(doc(db, getStudyContactsPath(), selectedStudy), {
        studyId: selectedStudy,
        contacts,
        systems,
        updatedAt: new Date()
      });
      alert("Contacts saved successfully");
    } catch (err) {
      console.error(err);
      alert("Error saving contacts");
    }
    setSaving(false);
  };

  const selectedLabel = studies.find((s) => s.id === selectedStudy)?.label;

  return (
    <div className="relative min-h-screen w-full text-slate-100 font-[Manrope]">
      <PageBackground />

      {/* Sticky header */}
      <header className="sticky top-0 z-30 backdrop-blur-2xl bg-slate-950/55 border-b border-white/10 shadow-[0_8px_30px_-12px_rgba(124,58,237,0.35)]">
        <div className="max-w-6xl mx-auto px-3 sm:px-4 py-2.5 flex items-center gap-3">
          <button
            onClick={() => window.history.back()}
            className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-white/[0.04] border border-white/10 hover:bg-white/[0.08] hover:border-white/20 text-xs text-slate-300 hover:text-white transition shrink-0"
          >
            <ArrowLeft className="w-3.5 h-3.5" />
            <span className="hidden sm:inline">Back</span>
          </button>

          <div className="flex items-center gap-2 min-w-0 flex-1">
            <div className="relative shrink-0">
              <div className="absolute inset-0 bg-gradient-to-br from-purple-500 to-blue-500 rounded-xl blur-md opacity-70" />
              <div className="relative w-9 h-9 rounded-xl bg-gradient-to-br from-purple-500/30 via-indigo-500/30 to-blue-500/30 border border-white/15 ring-1 ring-white/20 flex items-center justify-center">
                <User className="w-4 h-4 text-white" />
              </div>
            </div>
            <div className="min-w-0">
              <div className="text-[9px] uppercase tracking-[0.22em] text-purple-300/80 font-semibold leading-none">
                Lung Innovations · RegX
              </div>
              <h1 className="text-sm sm:text-base font-semibold tracking-tight text-white leading-tight truncate">
                Study Contacts
              </h1>
            </div>
          </div>

          {selectedStudy && (
            <button
              onClick={handleSave}
              disabled={saving}
              className="group relative inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-gradient-to-r from-purple-500 via-indigo-500 to-blue-500 ring-1 ring-white/20 text-xs font-semibold text-white hover:shadow-[0_8px_24px_-6px_rgba(139,92,246,0.6)] active:scale-[0.98] transition-all overflow-hidden disabled:opacity-60 disabled:cursor-not-allowed shrink-0"
            >
              <span className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/25 to-white/0 -translate-x-full group-hover:translate-x-full transition-transform duration-700" />
              <Save className="w-3.5 h-3.5 relative" />
              <span className="relative hidden sm:inline">{saving ? "Saving…" : "Save Contacts"}</span>
            </button>
          )}
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-3 sm:px-4 py-4 space-y-4">

        {/* Study selector */}
        <div
          className={`relative rounded-2xl border border-white/10 bg-white/[0.03] backdrop-blur-2xl overflow-visible ${
            showDropdown ? "z-[100]" : "z-10"
          }`}
          ref={dropdownRef}
        >
          <div className="absolute top-0 inset-x-0 h-[2px] bg-gradient-to-r from-purple-400 via-fuchsia-400 to-blue-400 opacity-70 pointer-events-none" />
          <div className="px-4 py-3">
            <div className="text-[10px] uppercase tracking-[0.22em] font-semibold text-slate-400 mb-2">Select Study</div>
            <button
              onClick={() => setShowDropdown(!showDropdown)}
              className="w-full flex items-center justify-between gap-2 px-3 py-2 rounded-xl bg-white/[0.04] border border-white/10 hover:bg-white/[0.06] text-sm text-slate-100 transition cursor-pointer text-left"
            >
              <span className={selectedLabel ? "text-slate-100" : "text-slate-500"}>
                {selectedLabel || "— Choose Study —"}
              </span>
              <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform shrink-0 ${showDropdown ? "rotate-180" : ""}`} />
            </button>

            {showDropdown && (
              <div className="study-contacts-dropdown absolute left-4 right-4 mt-1.5 z-[110] rounded-xl border border-white/10 bg-[#0c0826]/95 backdrop-blur-2xl shadow-[0_20px_60px_-15px_rgba(124,58,237,0.5)] max-h-72 overflow-hidden">
                <div className="study-contacts-dropdown-search sticky top-0 bg-[#0c0826]/95 backdrop-blur-2xl border-b border-white/10">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                    <input
                      type="text"
                      placeholder="Search studies…"
                      className="w-full pl-9 pr-3 py-2 bg-transparent text-sm text-slate-100 placeholder:text-slate-500 focus:outline-none"
                      value={studySearch}
                      onChange={(e) => setStudySearch(e.target.value)}
                    />
                  </div>
                </div>
                <ul className="overflow-y-auto max-h-56 py-1">
                  {filteredStudies.map((s) => (
                    <li
                      key={s.id}
                      onClick={() => handleStudySelect(s.id)}
                      className={`px-3 py-2 mx-1 rounded-lg cursor-pointer text-xs transition flex items-center gap-2 ${
                        selectedStudy === s.id
                          ? "bg-purple-500/15 text-white border border-purple-300/30"
                          : "text-slate-200 hover:bg-white/[0.05] border border-transparent"
                      }`}
                    >
                      {s.label}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>

        {/* Study info strip */}
        {selectedStudy && studyDetails && (
          <div className="relative rounded-2xl border border-white/10 bg-white/[0.03] backdrop-blur-2xl overflow-hidden">
            <div className="absolute top-0 inset-x-0 h-[2px] bg-gradient-to-r from-blue-400 via-cyan-400 to-teal-400 opacity-70 pointer-events-none" />
            <div className="px-4 py-3">
              <div className="text-[10px] uppercase tracking-[0.22em] font-semibold text-slate-400 mb-3">Study Information</div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                {[
                  { label: "Site ID", value: studyDetails.SITE_ID },
                  { label: "Enrollment Goal", value: studyDetails.GOAL || 0, cls: "text-purple-300" },
                  { label: "Enrolled", value: studyDetails.ENROLLED || 0, cls: "text-emerald-300" },
                  { label: "Screen Failures", value: studyDetails.SCREEN_FAILURES || 0, cls: "text-rose-300" },
                ].map(({ label, value, cls }) => (
                  <div key={label} className="rounded-xl bg-white/[0.03] border border-white/10 p-3 text-center">
                    <div className="text-[10px] uppercase tracking-wider text-slate-400 font-medium mb-1">{label}</div>
                    <div className={`text-xl font-bold ${cls || "text-white"}`}>{value}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Contact cards */}
        {selectedStudy && (
          <>
            <div className="relative rounded-2xl border border-white/10 bg-white/[0.03] backdrop-blur-2xl overflow-hidden">
              <div className="absolute top-0 inset-x-0 h-[2px] bg-gradient-to-r from-purple-400 via-indigo-400 to-blue-400 opacity-70 pointer-events-none" />
              <div className="px-4 py-3 border-b border-white/5 flex items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                  <User className="w-3.5 h-3.5 text-purple-300" />
                  <span className="text-[11px] uppercase tracking-[0.22em] font-semibold text-white">
                    Contacts
                  </span>
                  <span className="inline-flex items-center px-1.5 py-0.5 rounded-md text-[10px] font-mono bg-white/[0.06] border border-white/10 text-slate-300">
                    {contacts.length}
                  </span>
                </div>
                <button
                  onClick={addContact}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-purple-500/15 border border-purple-300/30 hover:bg-purple-500/25 text-xs font-semibold text-purple-200 hover:text-white transition"
                >
                  <Plus className="w-3.5 h-3.5" />
                  Add Contact
                </button>
              </div>

              <div className="p-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {contacts.map((c, i) => (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="relative rounded-xl border border-white/10 bg-white/[0.025] backdrop-blur-xl p-4 space-y-3"
                  >
                    <div className="absolute top-0 inset-x-0 h-[2px] bg-gradient-to-r from-purple-400 to-blue-400 opacity-50 rounded-t-xl pointer-events-none" />

                    <label className="block">
                      <span className={labelCls}>Role</span>
                      <input
                        list="roles"
                        value={c.role}
                        onChange={(e) => updateContact(i, "role", e.target.value)}
                        className={inputCls}
                        placeholder="Select or type role"
                      />
                    </label>

                    <label className="block">
                      <span className={labelCls}>Name</span>
                      <div className="relative">
                        <User className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-500 pointer-events-none" />
                        <input
                          value={c.name}
                          onChange={(e) => updateContact(i, "name", e.target.value)}
                          className={`${inputCls} pl-9`}
                          placeholder="Full name"
                        />
                      </div>
                    </label>

                    <label className="block">
                      <span className={labelCls}>Email</span>
                      <div className="relative">
                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-500 pointer-events-none" />
                        <input
                          value={c.email}
                          onChange={(e) => updateContact(i, "email", e.target.value)}
                          className={`${inputCls} pl-9`}
                          placeholder="email@example.com"
                        />
                      </div>
                    </label>

                    <div>
                      <span className={labelCls}>Phone Numbers</span>
                      <div className="space-y-2">
                        {c.phones.map((p, pIndex) => (
                          <div key={pIndex} className="flex gap-2 items-center">
                            <div className="relative">
                              <Phone className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3 h-3 text-slate-500 pointer-events-none" />
                              <input
                                list="countryCodes"
                                value={p.country}
                                onChange={(e) => updatePhone(i, pIndex, "country", e.target.value)}
                                className="w-20 pl-7 pr-2 py-2 rounded-xl bg-white/[0.04] border border-white/10 text-xs text-slate-100 focus:outline-none focus:border-purple-400/50 transition"
                                placeholder="+1"
                              />
                            </div>
                            <input
                              value={p.number}
                              onChange={(e) => updatePhone(i, pIndex, "number", e.target.value)}
                              placeholder="XXX-XXX-XXXX"
                              className={`${inputCls} flex-1`}
                            />
                            <button
                              onClick={() => deletePhone(i, pIndex)}
                              className="w-8 h-8 rounded-lg bg-rose-500/10 border border-rose-400/20 hover:bg-rose-500/20 flex items-center justify-center text-rose-300 hover:text-rose-100 transition shrink-0"
                            >
                              <Trash className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        ))}
                        <button
                          onClick={() => addPhone(i)}
                          className="inline-flex items-center gap-1 text-xs font-medium text-purple-300 hover:text-white transition"
                        >
                          <Plus className="w-3 h-3" /> Add Phone
                        </button>
                      </div>
                    </div>

                    <button
                      onClick={() => deleteContact(i)}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-rose-500/10 border border-rose-400/20 hover:bg-rose-500/20 text-xs font-medium text-rose-300 hover:text-rose-100 transition w-full justify-center"
                    >
                      <Trash className="w-3.5 h-3.5" />
                      Remove Contact
                    </button>
                  </motion.div>
                ))}

                {contacts.length === 0 && (
                  <div className="col-span-full rounded-xl border border-dashed border-white/10 bg-white/[0.02] p-8 text-center text-xs text-slate-500">
                    No contacts yet. Click "Add Contact" to get started.
                  </div>
                )}
              </div>
            </div>

            {/* Systems section */}
            <div className="relative rounded-2xl border border-white/10 bg-white/[0.03] backdrop-blur-2xl overflow-hidden">
              <div className="absolute top-0 inset-x-0 h-[2px] bg-gradient-to-r from-amber-400 via-orange-400 to-rose-400 opacity-70 pointer-events-none" />
              <div className="px-4 py-3 border-b border-white/5 flex items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                  <Layers className="w-3.5 h-3.5 text-amber-300" />
                  <span className="text-[11px] uppercase tracking-[0.22em] font-semibold text-white">
                    Systems Used in Study
                  </span>
                  <span className="inline-flex items-center px-1.5 py-0.5 rounded-md text-[10px] font-mono bg-white/[0.06] border border-white/10 text-slate-300">
                    {systems.length}
                  </span>
                </div>
                <button
                  onClick={addSystem}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-amber-500/15 border border-amber-300/30 hover:bg-amber-500/25 text-xs font-semibold text-amber-200 hover:text-white transition"
                >
                  <Plus className="w-3.5 h-3.5" />
                  Add System
                </button>
              </div>

              <div className="p-4 space-y-2">
                {systems.map((sys, i) => (
                  <div key={i} className="flex gap-2 items-center">
                    <input
                      value={sys}
                      onChange={(e) => updateSystem(i, e.target.value)}
                      placeholder="e.g. Medidata Rave, REDCap…"
                      className={`${inputCls} flex-1`}
                    />
                    <button
                      onClick={() => deleteSystem(i)}
                      className="w-8 h-8 rounded-lg bg-rose-500/10 border border-rose-400/20 hover:bg-rose-500/20 flex items-center justify-center text-rose-300 hover:text-rose-100 transition shrink-0"
                    >
                      <Trash className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}
                {systems.length === 0 && (
                  <div className="rounded-xl border border-dashed border-white/10 bg-white/[0.02] p-6 text-center text-xs text-slate-500">
                    No systems added yet.
                  </div>
                )}
              </div>
            </div>

            {/* Save footer */}
            <div className="flex justify-center pb-8">
              <button
                onClick={handleSave}
                disabled={saving}
                className="group relative inline-flex items-center gap-2 px-6 py-2.5 rounded-xl bg-gradient-to-r from-emerald-500 via-teal-500 to-cyan-500 ring-1 ring-white/20 text-sm font-semibold text-white hover:shadow-[0_10px_30px_-5px_rgba(16,185,129,0.6)] active:scale-[0.98] transition-all overflow-hidden disabled:opacity-60 disabled:cursor-not-allowed"
              >
                <span className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/25 to-white/0 -translate-x-full group-hover:translate-x-full transition-transform duration-700" />
                <CheckCircle2 className="w-4 h-4 relative" />
                <span className="relative">{saving ? "Saving…" : "Save Contacts"}</span>
              </button>
            </div>
          </>
        )}
      </div>

      <datalist id="roles">
        {STANDARD_ROLES.map((r) => (
          <option key={r} value={r} />
        ))}
      </datalist>
      <datalist id="countryCodes">
        <option value="+1">United States / Canada</option>
        <option value="+44">United Kingdom</option>
        <option value="+61">Australia</option>
        <option value="+91">India</option>
        <option value="+49">Germany</option>
        <option value="+33">France</option>
        <option value="+34">Spain</option>
        <option value="+39">Italy</option>
        <option value="+55">Brazil</option>
        <option value="+81">Japan</option>
        <option value="+86">China</option>
        <option value="+971">UAE</option>
        <option value="+65">Singapore</option>
      </datalist>
    </div>
  );
};

export default StudyContactsPage;
